import { notifications } from '@mantine/notifications';
import {
  CreateToppingCategoryPayload,
  CreateToppingPayload,
  UpdateToppingCategoryPayload,
  UpdateToppingPayload,
} from '@models/payload/topping';
import { IError, IErrorResponse } from '@models/response/error';
import { MenuCategoryToMenuItemSiteQueryResponse, ToppingCategoryDto } from '@models/ropolo';

import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import categoryService from '@services/category';
import toppingService from '@services/topping';
import { AxiosError } from 'axios';
import messages from '../shared/constants/messages';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

export type ToppingCategoryType = ToppingCategoryDto & {
  toppings: ToppingCategoryDto[];
  menuItemIds: string[];
};

export type MenuCategory = MenuCategoryToMenuItemSiteQueryResponse;

interface GetAllVendorMenuItemsResponse {
  data: {
    menuCategories: MenuCategory[];
  };
}
interface ToppingsStateInterface {
  loading: boolean;
  toppingCategories: ToppingCategoryType[];
  menuCategories: MenuCategory[];
  errors: IError | undefined;
}

const initialState: ToppingsStateInterface = {
  loading: false,
  toppingCategories: [],
  menuCategories: [],
  errors: undefined,
};

const createNewToppingCategory = createAppAsyncThunk<void, CreateToppingCategoryPayload>(
  'topping/createNewToppingCategory',
  async (input, { rejectWithValue }) => {
    try {
      const response = await toppingService.createTopping(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const removeToppingCategory = createAppAsyncThunk<void, string>('topping/removeToppingCategory', async (id) => {
  const response = await toppingService.removeTopping(id);
  notifications.show({
    message: messages.OPERATION_SUBMIT_SUCCESS,
    color: 'green',
  });
  return response;
});

const syncToppingCategoryWithSnapp = createAppAsyncThunk<void, string>(
  'topping/syncToppingCategoryWithSnapp',
  async (id) => {
    const response = await toppingService.syncToppingCategoryWithSnapp(id);
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  }
);

const editToppingCategory = createAppAsyncThunk<void, UpdateToppingCategoryPayload>(
  'topping/editToppingCategory',
  async (input, { rejectWithValue }) => {
    try {
      const response = await toppingService.updateTopping(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const getAllVendorMenuItems = createAppAsyncThunk<GetAllVendorMenuItemsResponse>(
  'topping/getAllVendorMenuItems',
  async () => {
    const res = await categoryService.getAllMenuCategoriesWithMenuItems();
    const response = {
      data: {
        menuCategories: res.data || [],
      },
    };
    return response;
  }
);

const createNewToppingItem = createAppAsyncThunk<void, CreateToppingPayload>(
  'topping/createNewToppingItem',
  async (input, { rejectWithValue }) => {
    try {
      const response = await toppingService.createToppingItem(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const removeToppingItem = createAppAsyncThunk<void, string>('topping/removeToppingItem', async (id) => {
  const response = await toppingService.removeToppingItem(id);
  notifications.show({
    message: messages.OPERATION_SUBMIT_SUCCESS,
    color: 'green',
  });
  return response;
});

const editToppingItem = createAppAsyncThunk<void, UpdateToppingPayload>(
  'topping/editToppingItem',
  async (input, { rejectWithValue }) => {
    try {
      const response = await toppingService.updateToppingItem(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const changeToppingItemAvailability = createAppAsyncThunk<void, string>(
  'topping/changeToppingItemAvailability',
  async (input) => {
    const response = await toppingService.changeToppingAvailability(input);
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  }
);

/// Slice
const toppingsSlice = createSlice({
  name: 'toppings',
  initialState,
  reducers: {
    clearErrors: (state) => ({
      ...state,
      errors: undefined,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<ToppingsStateInterface>) => {
    builder.addCase(createNewToppingCategory.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(createNewToppingCategory.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(createNewToppingCategory.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(removeToppingCategory.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(removeToppingCategory.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(removeToppingCategory.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(syncToppingCategoryWithSnapp.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(syncToppingCategoryWithSnapp.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(syncToppingCategoryWithSnapp.rejected, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(editToppingCategory.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(editToppingCategory.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(editToppingCategory.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(getAllVendorMenuItems.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(getAllVendorMenuItems.fulfilled, (state, action) => {
      const { data } = action.payload;
      const { menuCategories } = data;

      return {
        ...state,
        loading: false,
        menuCategories,
      };
    });

    builder.addCase(createNewToppingItem.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(createNewToppingItem.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(createNewToppingItem.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(editToppingItem.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(editToppingItem.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(editToppingItem.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(removeToppingItem.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(removeToppingItem.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(removeToppingItem.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(changeToppingItemAvailability.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(changeToppingItemAvailability.fulfilled, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));

    builder.addCase(changeToppingItemAvailability.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default toppingsSlice;
export {
  changeToppingItemAvailability,
  createNewToppingCategory,
  createNewToppingItem,
  editToppingCategory,
  editToppingItem,
  getAllVendorMenuItems,
  removeToppingCategory,
  removeToppingItem,
  syncToppingCategoryWithSnapp,
};
